import React, { useCallback, useEffect, useState } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Toaster } from "./components/ui/sonner";
import { offlineManager } from "./lib/offlineManager";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { AutoTranslate, LanguageProvider } from "./i18n/LanguageContext";
import Login from "./pages/Login";
import MainApp from "./pages/MainApp";
const Kiosk = React.lazy(() => import("./pages/Kiosk"));
import OfflineStatusIndicator from "./components/OfflineStatusIndicator";
import OfflineBanner from "./components/OfflineBanner";
import { APP_VERSION } from "./version";

const LoginRoute = () => {
  const { user } = useAuth();
  return user ? <Navigate to="/" replace /> : <Login />;
};

const ProtectedRoute = () => {
  const { user } = useAuth();
  return user ? <MainApp /> : <Navigate to="/login" replace />;
};

const KioskRoute = () => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  return (
    <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-gray-950 text-white">Chargement de la borne…</div>}>
      <Kiosk />
    </React.Suspense>
  );
};

// Composant principal App avec gestion hors ligne optimisée
function App() {
  const [updateReady, setUpdateReady] = useState(false);
  const [availableVersion, setAvailableVersion] = useState("");

  const fetchAvailableVersion = useCallback(async () => {
    try {
      const response = await fetch(`/version-config.js?check=${Date.now()}`, { cache: 'no-store' });
      const config = await response.text();
      const match = config.match(/version:\s*'([^']+)'/);
      if (match?.[1]) setAvailableVersion(match[1]);
    } catch (_) {
      setAvailableVersion("");
    }
  }, []);
  // Enregistrer le service worker - Version optimisée pour dev et prod
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      const registerSW = async () => {
        try {
          const registration = await navigator.serviceWorker.register('/sw.js', {
            scope: '/',
            updateViaCache: 'none'
          });

          console.log('🎯 Service Worker enregistré:', registration);

          registration.addEventListener('updatefound', () => {
            console.log('🔄 Nouvelle version Service Worker détectée');
            const newWorker = registration.installing;

            if (newWorker) {
              newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                  fetchAvailableVersion();
                  setUpdateReady(true);
                }
              });
            }
          });

          if ('sync' in window.ServiceWorkerRegistration.prototype) {
            try {
              await registration.sync.register('background-sync');
              console.log('✅ Background Sync configuré');
            } catch (error) {
              console.warn('⚠️ Background Sync non disponible:', error);
            }
          }

          setInterval(() => {
            registration.update();
          }, 60000);

        } catch (error) {
          console.error('❌ Erreur Service Worker:', error);
        }
      };

      registerSW();

      const handleSWMessage = (event) => {
        console.log('📩 Message SW reçu:', event.data);

        if (event.data && event.data.type) {
          switch (event.data.type) {
            case 'SW_ACTIVATED':
              console.log(`✅ Service Worker activé - ${event.data.version}`);
              if (event.data.version) setAvailableVersion(event.data.version);
              break;
            case 'SYNC_COMPLETED':
              const { synced, failed } = event.data.data || {};
              if (synced > 0) {
                toast.success(`📱 ${synced} éléments synchronisés`);
              }
              if (failed > 0) {
                toast.warning(`⚠️ ${failed} éléments échoués`);
              }
              break;
            case 'CACHE_UPDATED':
              console.log('📦 Cache mis à jour');
              break;
            default:
              console.log('⚠️ Message SW inconnu:', event.data.type);
          }
        }
      };

      navigator.serviceWorker.addEventListener('message', handleSWMessage);
      navigator.serviceWorker.ready.then((registration) => {
        if (registration.waiting) {
          fetchAvailableVersion();
          setUpdateReady(true);
        }
      });

      return () => {
        navigator.serviceWorker.removeEventListener('message', handleSWMessage);
      };
    } else {
      console.warn('⚠️ Service Worker non supporté dans ce navigateur');
    }

    if (navigator.onLine) {
      offlineManager.triggerSync().catch(err => {
        console.log('Synchronisation initiale échouée:', err);
      });
    }
  }, [fetchAvailableVersion]);

  // Toast récapitulatif automatique à la fin d'une synchronisation
  useEffect(() => {
    const unsubscribe = offlineManager.onSyncComplete(({ synced, dropped, authErrors, needsReauth }) => {
      if (synced > 0) {
        toast.success(`✅ ${synced} élément${synced > 1 ? 's' : ''} synchronisé${synced > 1 ? 's' : ''}`);
      }
      const abandoned = (dropped || 0) + (authErrors || 0);
      if (abandoned > 0) {
        toast.warning(`⚠️ ${abandoned} élément${abandoned > 1 ? 's' : ''} abandonné${abandoned > 1 ? 's' : ''} (requête invalide)`);
      }
      if (needsReauth) {
        toast.warning("🔐 Session expirée : reconnectez-vous en ligne pour synchroniser vos saisies hors-ligne (aucune donnée n'est perdue).");
      }
    });
    return unsubscribe;
  }, []);

  return (
    <AuthProvider>
      <LanguageProvider>
      <AutoTranslate />
      <BrowserRouter>
        <div className="App">
          <Toaster />
          <OfflineBanner />
          {updateReady && (
            <div
              className="fixed top-0 left-0 right-0 z-[100] bg-orange-600 text-white px-4 py-3 flex flex-wrap items-center justify-center gap-3 shadow-lg"
              data-testid="update-available-banner"
            >
              <span className="text-sm font-medium">
                🔄 Une nouvelle mise à jour{availableVersion ? ` (${availableVersion})` : ''} est disponible.
              </span>
              <button
                onClick={() => window.location.reload()}
                className="bg-white text-orange-700 text-sm font-semibold px-4 py-1.5 rounded-full hover:bg-orange-50 transition-colors"
                data-testid="update-reload-button"
              >
                Actualiser
              </button>
            </div>
          )}
          <OfflineStatusIndicator />
          <Routes>
            <Route path="/login" element={<LoginRoute />} />
            <Route path="/borne" element={<KioskRoute />} />
            <Route path="/" element={<ProtectedRoute />} />
          </Routes>
        </div>
      </BrowserRouter>
      </LanguageProvider>
    </AuthProvider>
  );
}

export default App;
